// src/stores/GBPStore.js - Zustand Store for Google Business Profile integration
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useGBPStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE
    // ================================

    // Connection status
    gbpConnection: null,
    gbpConnectionLoading: false,

    // OAuth flow state (DIFFERENT from Stripe - handles redirect)
    oauthUrl: null,
    oauthInitiating: false,

    // Accounts & Locations
    accounts: [],
    accountsLoading: false,

    locations: [],
    locationsLoading: false,

    selectedLocations: [], // User's selection before saving
    savingLocations: false,

    // Reviews
    reviews: [],
    reviewsLoading: false,
    reviewsPagination: null,

    // Review operations
    replyingToReview: null, // Review ID being replied to
    deletingReplyForReview: null, // Review ID having reply deleted

    // Review request link
    reviewLink: null,
    reviewLinkLoading: false,

    // Disconnection
    disconnecting: false,

    // Connection state
    isConnected: false,

    // Cache state
    cache: {
      gbpConnection: null,
      accounts: [],
      locations: [],
      reviews: [],
      lastFetch: null
    },

    // Internal flags
    _initialized: false,

    // ================================
    // SOCKET OPERATIONS (NO RETURN VALUES)
    // ================================

    /**
     * Load GBP connection status - NO RETURN VALUE
     */
    loadGBPConnectionStatus: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ GBPStore: Socket service not initialized');
        return;
      }

      console.log('🔌 GBPStore: Loading connection status');
      set({
        gbpConnectionLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_GBP_CONNECTION_STATUS, {});
    },

    /**
     * Start OAuth flow - generates OAuth URL
     * DIFFERENT from Stripe: Frontend will redirect user to Google
     */
    startGBPOAuth: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ GBPStore: Socket service not initialized');
        return;
      }

      console.log('🚀 GBPStore: Starting OAuth flow');
      set({
        oauthInitiating: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.START_GBP_OAUTH, {});
    },

    /**
     * Fetch GBP accounts - called after OAuth success
     */
    fetchGBPAccounts: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ GBPStore: Socket service not initialized');
        return;
      }

      console.log('📋 GBPStore: Fetching accounts');
      set({
        accountsLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.FETCH_GBP_ACCOUNTS, {});
    },

    /**
     * Fetch locations for an account
     * @param {string} accountName - Account resource name (e.g., 'accounts/123')
     */
    fetchGBPLocations: (accountName) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ GBPStore: Socket service not initialized');
        return;
      }

      if (!accountName) {
        console.error('❌ GBPStore: Account name is required');
        return;
      }

      console.log('📍 GBPStore: Fetching locations for account:', accountName);
      set({
        locationsLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.FETCH_GBP_LOCATIONS, {
        account_name: accountName
      });
    },

    /**
     * Save selected locations
     * @param {array} locations - Array of location objects
     */
    saveGBPLocations: (locations) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ GBPStore: Socket service not initialized');
        return;
      }

      if (!locations || locations.length === 0) {
        console.error('❌ GBPStore: At least one location must be selected');
        return;
      }

      console.log('💾 GBPStore: Saving locations:', locations.length);
      set({
        savingLocations: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.SAVE_GBP_LOCATIONS, {
        locations
      });
    },

    /**
     * Fetch reviews for a location
     * @param {string} locationName - Location resource name
     * @param {object} options - Pagination options
     */
    fetchGBPReviews: (locationName, options = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ GBPStore: Socket service not initialized');
        return;
      }

      if (!locationName) {
        console.error('❌ GBPStore: Location name is required');
        return;
      }

      const { pageSize = 50, pageToken = null } = options;

      console.log('⭐ GBPStore: Fetching reviews for:', locationName);
      set({
        reviewsLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.FETCH_GBP_REVIEWS, {
        location_name: locationName,
        page_size: pageSize,
        page_token: pageToken
      });
    },

    /**
     * Reply to a review
     * @param {string} locationName - Location resource name
     * @param {string} reviewId - Review ID
     * @param {string} replyText - Reply content
     */
    replyToGBPReview: (locationName, reviewId, replyText) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ GBPStore: Socket service not initialized');
        return;
      }

      if (!locationName || !reviewId || !replyText) {
        console.error('❌ GBPStore: Location, review ID, and reply text are required');
        return;
      }

      console.log('💬 GBPStore: Replying to review:', reviewId);
      set({
        replyingToReview: reviewId
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.REPLY_TO_GBP_REVIEW, {
        location_name: locationName,
        review_id: reviewId,
        reply_text: replyText
      });
    },

    /**
     * Delete a review reply
     * @param {string} locationName - Location resource name
     * @param {string} reviewId - Review ID
     */
    deleteGBPReviewReply: (locationName, reviewId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ GBPStore: Socket service not initialized');
        return;
      }

      if (!locationName || !reviewId) {
        console.error('❌ GBPStore: Location and review ID are required');
        return;
      }

      console.log('🗑️ GBPStore: Deleting reply for review:', reviewId);
      set({
        deletingReplyForReview: reviewId
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_GBP_REVIEW_REPLY, {
        location_name: locationName,
        review_id: reviewId
      });
    },

    /**
     * Get review request link for a location
     * @param {string} locationName - Location resource name (optional, uses primary if not provided)
     */
    getGBPReviewLink: (locationName = null) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        return;
      }

      console.log('🔗 GBPStore: Getting review link');
      set({
        reviewLinkLoading: true
      });

      const data = locationName ? { location_name: locationName } : {};
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_GBP_REVIEW_LINK, data);
    },

    /**
     * Disconnect GBP integration
     */
    disconnectGBP: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ GBPStore: Socket service not initialized');
        return;
      }

      console.log('🔌 GBPStore: Disconnecting GBP');
      set({
        disconnecting: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.DISCONNECT_GBP, {});
    },

    // ================================
    // UTILITY FUNCTIONS
    // ================================

    /**
     * Get connection status string
     */
    getConnectionStatus: () => {
      const { gbpConnection } = get();
      return gbpConnection?.connection_status || 'disconnected';
    },

    /**
     * Check if connected
     */
    isGBPConnected: () => {
      const status = get().getConnectionStatus();
      return status === 'connected';
    },

    /**
     * Check if token is expired
     */
    isTokenExpired: () => {
      const { gbpConnection } = get();
      return gbpConnection?.token_expired === true;
    },

    /**
     * Get user-friendly status message
     */
    getStatusMessage: () => {
      const status = get().getConnectionStatus();
      const messages = {
        disconnected: 'Not connected to Google Business Profile',
        connected: 'Connected to Google Business Profile',
        token_expired: 'Connection expired - please reconnect'
      };
      return messages[status] || 'Unknown status';
    },

    /**
     * Update selected locations (for UI state before saving)
     */
    setSelectedLocations: (locations) => {
      set({ selectedLocations: locations });
    },

    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================

    _setGBPConnectionLoaded: (data) => {
      console.log('📡 GBPStore: Connection status received');

      set(state => ({
        gbpConnection: data,
        gbpConnectionLoading: false,
        isConnected: data?.connection_status === 'connected',
        cache: {
          ...state.cache,
          gbpConnection: data,
          lastFetch: new Date()
        }
      }));
    },

    _setOAuthUrlGenerated: (data) => {
      console.log('📡 GBPStore: OAuth URL generated');

      set({
        oauthUrl: data.url,
        oauthInitiating: false
      });
    },

    _setAccountsLoaded: (data) => {
      console.log('📡 GBPStore: Accounts loaded:', data.count);

      set(state => ({
        accounts: data.accounts,
        accountsLoading: false,
        cache: {
          ...state.cache,
          accounts: data.accounts
        }
      }));
    },

    _setLocationsLoaded: (data) => {
      console.log('📡 GBPStore: Locations loaded:', data.count);

      set(state => ({
        locations: data.locations,
        locationsLoading: false,
        cache: {
          ...state.cache,
          locations: data.locations
        }
      }));
    },

    _setLocationsSaved: (data) => {
      console.log('📡 GBPStore: Locations saved');

      set(state => ({
        savingLocations: false,
        selectedLocations: [],
        // Update connection with new locations
        gbpConnection: {
          ...state.gbpConnection,
          connected_locations: data.locations
        }
      }));
    },

    _setReviewsLoaded: (data) => {
      console.log('📡 GBPStore: Reviews loaded:', data.count);

      set(state => ({
        reviews: data.reviews,
        reviewsLoading: false,
        reviewsPagination: {
          nextPageToken: data.nextPageToken,
          count: data.count
        },
        cache: {
          ...state.cache,
          reviews: data.reviews
        }
      }));
    },

    _setReviewReplyPosted: (data) => {
      console.log('📡 GBPStore: Review reply posted');

      set({
        replyingToReview: null
      });

      // Update the review in the list with the new reply
      // Backend will broadcast this, so all users see it
    },

    _setReviewReplyDeleted: (data) => {
      console.log('📡 GBPStore: Review reply deleted');

      set({
        deletingReplyForReview: null
      });
    },

    _setReviewLinkGenerated: (data) => {
      console.log('📡 GBPStore: Review link generated');

      set({
        reviewLink: data,
        reviewLinkLoading: false
      });
    },

    _setGBPDisconnected: (data) => {
      console.log('📡 GBPStore: GBP disconnected');

      set({
        gbpConnection: null,
        disconnecting: false,
        isConnected: false,
        accounts: [],
        locations: [],
        selectedLocations: [],
        reviews: [],
        reviewsPagination: null,
        reviewLink: null,
        cache: {
          gbpConnection: null,
          accounts: [],
          locations: [],
          reviews: [],
          lastFetch: null
        }
      });
    },

    // ================================
    // SOCKET CONNECTION MANAGEMENT (following CustomerStore pattern)
    // ================================

    _setConnectionState: (isConnected) => {
      set({ isConnected });
    },

    _getSocketManager: () => {
      // Access socket manager from window global (following pattern)
      if (typeof window !== 'undefined' && window.__socketManager) {
        return { socketManager: window.__socketManager };
      }
      return { socketManager: null };
    },

    _handleAuthReset: () => {
      console.log('🧹 GBPStore: Resetting all state due to auth reset');

      set({
        // Reset all state to initial values
        gbpConnection: null,
        gbpConnectionLoading: false,
        oauthUrl: null,
        oauthInitiating: false,
        accounts: [],
        accountsLoading: false,
        locations: [],
        locationsLoading: false,
        selectedLocations: [],
        savingLocations: false,
        reviews: [],
        reviewsLoading: false,
        reviewsPagination: null,
        replyingToReview: null,
        deletingReplyForReview: null,
        reviewLink: null,
        reviewLinkLoading: false,
        disconnecting: false,
        isConnected: false,
        cache: {
          gbpConnection: null,
          accounts: [],
          locations: [],
          reviews: [],
          lastFetch: null
        },
        _initialized: false
      });
    },

    // ================================
    // INITIALIZATION (following CustomerStore pattern)
    // ================================

    _initialize: (socketManager) => {
      const state = get();

      console.log('🔌 GBPStore: Initializing with socket manager');

      // Store socket manager reference
      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }

      set({
        _initialized: true,
        isConnected: socketManager?.isAuthenticated || false
      });

      // Auto-load GBP connection status if authenticated
      if (socketManager?.isAuthenticated) {
        console.log('🔄 GBPStore: Auto-loading GBP connection status (first time)');
        state.loadGBPConnectionStatus();
      }
    }
  }))
);

// ================================
// SOCKET EVENT LISTENER INITIALIZATION
// ================================

/**
 * Initialize socket event listeners for GBPStore
 * Called by SocketContext on auth success
 */
export const initializeGBPStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    console.error('❌ GBPStore: Cannot initialize - socketManager not available');
    return;
  }

  console.log('🎧 GBPStore: Initializing socket listeners');

  const store = useGBPStore.getState();

  // Mark as initialized
  useGBPStore.setState({ _initialized: true });

  // Central event handler
  const handleSocketEvent = (eventName, responseData) => {
    const store = useGBPStore.getState();

    switch (eventName) {
      // ✅ SUCCESS EVENTS
      case SOCKET_EVENTS.GBP_CONNECTION_STATUS_LOADED:
        store._setGBPConnectionLoaded(responseData.data);
        break;

      case SOCKET_EVENTS.GBP_OAUTH_URL_GENERATED:
        store._setOAuthUrlGenerated(responseData.data);
        break;

      case SOCKET_EVENTS.GBP_ACCOUNTS_LOADED:
        store._setAccountsLoaded(responseData.data);
        break;

      case SOCKET_EVENTS.GBP_LOCATIONS_LOADED:
        store._setLocationsLoaded(responseData.data);
        break;

      case SOCKET_EVENTS.GBP_LOCATIONS_SAVED:
        store._setLocationsSaved(responseData.data);
        break;

      case SOCKET_EVENTS.GBP_REVIEWS_LOADED:
        store._setReviewsLoaded(responseData.data);
        break;

      case SOCKET_EVENTS.GBP_REVIEW_REPLY_POSTED:
        store._setReviewReplyPosted(responseData.data);
        break;

      case SOCKET_EVENTS.GBP_REVIEW_REPLY_DELETED:
        store._setReviewReplyDeleted(responseData.data);
        break;

      case SOCKET_EVENTS.GBP_REVIEW_LINK_GENERATED:
        store._setReviewLinkGenerated(responseData.data);
        break;

      case SOCKET_EVENTS.GBP_DISCONNECTED:
        store._setGBPDisconnected(responseData.data);
        break;

      // ❌ ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_GBP_CONNECTION_STATUS_FAILED:
        useGBPStore.setState({ gbpConnectionLoading: false });
        break;

      case SOCKET_EVENTS.START_GBP_OAUTH_FAILED:
        useGBPStore.setState({ oauthInitiating: false });
        break;

      case SOCKET_EVENTS.FETCH_GBP_ACCOUNTS_FAILED:
        useGBPStore.setState({ accountsLoading: false });
        break;

      case SOCKET_EVENTS.FETCH_GBP_LOCATIONS_FAILED:
        useGBPStore.setState({ locationsLoading: false });
        break;

      case SOCKET_EVENTS.SAVE_GBP_LOCATIONS_FAILED:
        useGBPStore.setState({ savingLocations: false });
        break;

      case SOCKET_EVENTS.FETCH_GBP_REVIEWS_FAILED:
        useGBPStore.setState({ reviewsLoading: false });
        break;

      case SOCKET_EVENTS.REPLY_TO_GBP_REVIEW_FAILED:
        useGBPStore.setState({ replyingToReview: null });
        break;

      case SOCKET_EVENTS.DELETE_GBP_REVIEW_REPLY_FAILED:
        useGBPStore.setState({ deletingReplyForReview: null });
        break;

      case SOCKET_EVENTS.DISCONNECT_GBP_FAILED:
        useGBPStore.setState({ disconnecting: false });
        console.log('❌ GBPStore: Disconnect failed -', responseData.message);
        break;

      default:
        console.warn('⚠️ GBPStore: Unhandled event:', eventName);
    }
  };

  // Set up individual event listeners
  const gbpEvents = [
    // Success events
    SOCKET_EVENTS.GBP_CONNECTION_STATUS_LOADED,
    SOCKET_EVENTS.GBP_OAUTH_URL_GENERATED,
    SOCKET_EVENTS.GBP_ACCOUNTS_LOADED,
    SOCKET_EVENTS.GBP_LOCATIONS_LOADED,
    SOCKET_EVENTS.GBP_LOCATIONS_SAVED,
    SOCKET_EVENTS.GBP_REVIEWS_LOADED,
    SOCKET_EVENTS.GBP_REVIEW_REPLY_POSTED,
    SOCKET_EVENTS.GBP_REVIEW_REPLY_DELETED,
    SOCKET_EVENTS.GBP_REVIEW_LINK_GENERATED,
    SOCKET_EVENTS.GBP_DISCONNECTED,
    // Error events
    SOCKET_EVENTS.GET_GBP_CONNECTION_STATUS_FAILED,
    SOCKET_EVENTS.START_GBP_OAUTH_FAILED,
    SOCKET_EVENTS.FETCH_GBP_ACCOUNTS_FAILED,
    SOCKET_EVENTS.FETCH_GBP_LOCATIONS_FAILED,
    SOCKET_EVENTS.SAVE_GBP_LOCATIONS_FAILED,
    SOCKET_EVENTS.FETCH_GBP_REVIEWS_FAILED,
    SOCKET_EVENTS.REPLY_TO_GBP_REVIEW_FAILED,
    SOCKET_EVENTS.DELETE_GBP_REVIEW_REPLY_FAILED,
    SOCKET_EVENTS.DISCONNECT_GBP_FAILED
  ];

  gbpEvents.forEach(eventName => {
    socketManager.universalHandler.on(eventName, (data) => {
      handleSocketEvent(eventName, { event: eventName, data });
    });
  });

  // Set up auth reset listener
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useGBPStore.getState()._handleAuthReset();
    });
  }

  // Initialize the store
  useGBPStore.getState()._initialize(socketManager);

  console.log('✅ GBPStore: Socket listeners initialized');
};

export default useGBPStore;
